import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { withTheme } from 'styled-components';
import { styled } from '../../utils/styled';
import { propStyles } from '../../utils/propStyles';
import DocumentMenuListItem from './DocumentMenuListItem';
import ClickLayer from '../ClickLayer';
import { computed } from 'mobx';
import { DataStore, SuperscriptScope } from '../../stores';
import { DisplayOption } from '../../types';
import DocumentsGrid from './DocumentsGrid';
import { KEYBOARD_KEYS } from '../../constants';
import { DocumentsGrid as DocumentsGridInterface } from '../../models/configuration/documents/DocumentsGrid';
import { ColumnDocumentsGrid } from '../../models/configuration/documents/ColumnDocumentsGrid';

const defaultDropdownProps = {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    minWidth: '160px',
    maxWidth: '400px',
    maxHeight: '240px',
    overflowY: 'auto',
    bgColor: 'white',
    boxShadow: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    zIndex: 'dropdown',
};

const DropDownDocumentsGrid = styled<any, any>(
    'div',
    'dropDownDocumentsGrid',
    'documentMenuList'
)`
    ${propStyles};
    top: 100%;
    right: 0;
    margin: 0;
    padding: 0;
`;

// max width set to 618px due to default sm screen size being 640px.
DropDownDocumentsGrid.defaultProps = {
    ...defaultDropdownProps,
    maxWidth: '618px',
    minWidth: '618px',
    maxHeight: 'none',
};

const InlineDocumentsGrid = styled<any, any>(
    'div',
    'inlineDocumentsGrid',
    'documentMenuList'
)`
    ${propStyles};
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;
InlineDocumentsGrid.defaultProps = {};

// Drop Down
const DropDownDocumentList = styled<any, any>(
    'ul',
    'dropDownDocumentList',
    'documentMenuList'
)`
    ${propStyles};
    margin: 0;
    padding: 0;
`;

DropDownDocumentList.defaultProps = defaultDropdownProps;

// Inline
const InlineDocumentList = styled<any, any>(
    'ul',
    'inlineDocumentList',
    'documentMenuList'
)`
    ${propStyles};
`;

InlineDocumentList.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 100%',
    flexWrap: 'nowrap',
    margin: '1em 0 0 0',
    padding: '0px',
    listStyle: 'none',
};

interface DocumentMenuListTheme {
    DropDownDocumentList?: any;
    InlineDocumentList?: any;
    DocumentMenuListItem?: any;
}

interface DocumentMenuListProps {
    theme: any;
    documents: Array<any>;
    open?: boolean;
    showLabel?: boolean;
    showCheckbox?: boolean;
    refContent: any;
    displayOption?: DisplayOption;
    metricContext?: string;
    documentType?: string;
    superscriptScope?: SuperscriptScope;
    dataStore: DataStore;
    columnDocumentsGrid?: ColumnDocumentsGrid;
    toggleDropdown(): void;
    /**
     * @title On Change
     *
     * @param document the document entity
     * @param key the key used to group the selected document by the entity
     */
    onChange?(document: any, key: string): void;
    selected?(clientCode: string): boolean;
}

@inject('dataStore')
@observer
class DocumentMenuList extends Component<DocumentMenuListProps> {
    @computed
    get theme() {
        const { theme } = this.props;
        const componentTheme: DocumentMenuListTheme = {};
        return { ...theme, ...componentTheme };
    }

    @computed
    get displayOption() {
        return this.props.displayOption || 'dropdown';
    }

    @computed
    get documentListItems(): Array<React.ReactNode> {
        const {
            theme,
            documents = [],
            showLabel = true,
            showCheckbox = false,
            metricContext,
            documentType,
            superscriptScope,
            onChange,
            selected,
        } = this.props;

        return documents.map(document => (
            <DocumentMenuListItem
                key={document.clientCode}
                document={document}
                showLabel={showLabel}
                showCheckbox={showCheckbox}
                onChange={onChange}
                selected={selected && selected(document.clientCode)}
                theme={theme}
                metricContext={metricContext}
                documentType={documentType}
                superscriptScope={superscriptScope}
                {...theme.DocumentListItem}
            />
        ));
    }

    @computed
    get documentsGridConfig(): DocumentsGridInterface {
        // documentType is going to be false here if no documents are loaded.
        const { documentType, columnDocumentsGrid } = this.props;
        const {
            documentsGrid,
            documentsGridConditionals = [],
        } = this.props.dataStore.config;

        const { config: documentsGridConditionalConfig } =
            documentsGridConditionals.find(d =>
                d?.documentTypes?.includes(documentType)
            ) || {};

        return (
            documentsGridConditionalConfig ||
            documentsGrid ||
            columnDocumentsGrid?.documentsGrid
        );
    }

    @computed
    get uiOptions() {
        const { uiOptions = {} } = this.documentsGridConfig;
        return uiOptions;
    }

    @computed
    get documentsGrid(): React.ReactNode {
        const {
            superscriptScope,
            documents,
            documentType,
            showCheckbox,
            columnDocumentsGrid,
            onChange,
            selected,
        } = this.props;

        return (
            <DocumentsGrid
                config={this.documentsGridConfig}
                superscriptScope={superscriptScope}
                documents={documents}
                documentType={documentType}
                showCheckbox={showCheckbox}
                toggleDownload={onChange}
                documentSelected={selected}
                columnId={columnDocumentsGrid?.id}
                columnDocumentsSearch={
                    columnDocumentsGrid?.documentSearchRequests
                }
            />
        );
    }

    @computed
    get displayDocumentsGrid(): boolean {
        return Boolean(this.documentsGridConfig);
    }

    onKeyDown = event => {
        const { toggleDropdown } = this.props;
        if (event.key === KEYBOARD_KEYS.ESCAPE) {
            toggleDropdown();
        }
    };

    @computed
    get documentsGridDisplay() {
        const { refContent } = this.props;
        if (this.displayOption === 'dropdown') {
            return (
                <DropDownDocumentsGrid
                    ref={refContent}
                    onKeyDown={this.onKeyDown}
                    {...this.theme.DocumentMenuGrid}
                    {...this.uiOptions?.dropdown?.styleProps}
                >
                    {this.documentsGrid}
                </DropDownDocumentsGrid>
            );
        }
        return (
            <InlineDocumentsGrid {...this.theme.DocumentMenuGrid}>
                {this.documentsGrid}
            </InlineDocumentsGrid>
        );
    }

    @computed
    get documentsListDisplay() {
        const { refContent, theme } = this.props;
        if (this.displayOption === 'dropdown') {
            return (
                <DropDownDocumentList
                    ref={refContent}
                    onKeyDown={this.onKeyDown}
                    {...theme.DocumentList}
                >
                    {this.documentListItems}
                </DropDownDocumentList>
            );
        }
        return (
            <InlineDocumentList {...theme.DocumentList}>
                {this.documentListItems}
            </InlineDocumentList>
        );
    }

    render() {
        const {
            open = false,
            toggleDropdown,
            displayOption = 'dropdown',
        } = this.props;

        // displayOption can be dropdown or inline
        // Both scenarios need another option to switch between documentListItems and DocumentsGrid
        const documentsDisplay = this.displayDocumentsGrid
            ? this.documentsGridDisplay
            : this.documentsListDisplay;
        return (
            open && (
                <Fragment>
                    {displayOption === 'dropdown' && (
                        <ClickLayer
                            onClick={e => {
                                e.stopPropagation();
                                toggleDropdown();
                            }}
                            data-metric-id={'documentMenuList-clickLayer'}
                        />
                    )}
                    {documentsDisplay}
                </Fragment>
            )
        );
    }
}

export default withTheme(DocumentMenuList) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DocumentMenuList.tsx